import React from "react";
import { ASSETS } from "../lib/assets";
import { CTA, Eyebrow } from "../components/CTA";
import { useReveal } from "../lib/hooks";

export default function Proyectos() {
  return (
    <main data-testid="proyectos-page" className="bg-[var(--pc-bg)]">
      <ProyectosHero />
      <CategoryFeature
        id="residencias"
        testId="residencias-section"
        number="01"
        title="Residencias"
        body="Tu casa no debería parecerse a ninguna otra. Debe responder a tu forma de vivir, a tu ritmo, a tu familia, a la luz del lote y a la calma que quieres construir."
        image={ASSETS.proyectosResidencias}
        ctaLabel="Ver residencias"
        ctaTo="/proyectos/residencias"
        ctaTestId="cta-residencias"
      />
      <CategoryFeature
        id="enclaves"
        testId="enclaves-section"
        number="02"
        title="Enclaves"
        body="Cuando el proyecto supera la casa, la arquitectura debe ordenar el territorio. Diseñamos comunidades privadas donde paisaje, operación, inversión y experiencia habitan una misma visión."
        image={ASSETS.proyectosEnclaves}
        ctaLabel="Ver enclaves"
        ctaTo="/proyectos/enclaves"
        ctaTestId="cta-enclaves"
      />
    </main>
  );
}

/* ============================== HERO — cenital full-bleed + left fade ============================== */
function ProyectosHero() {
  const head = useReveal();
  const body = useReveal();
  const choices = useReveal({ threshold: 0.3 });
  const cta = useReveal();
  const img = useReveal({ threshold: 0.08 });

  return (
    <section
      data-testid="proyectos-hero"
      className="relative w-full overflow-hidden min-h-[78vh] md:min-h-screen"
    >
      <div
        ref={img.ref}
        className={`absolute inset-0 reveal-img ${img.revealed ? "is-in" : ""}`}
      >
        <img
          src={ASSETS.proyectosHero}
          alt="Cenital de un proyecto de Paco Cabrera"
          className="absolute inset-0 w-full h-full object-cover"
          draggable="false"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(7,7,7,0.97) 0%, rgba(7,7,7,0.92) 22%, rgba(7,7,7,0.62) 38%, rgba(7,7,7,0.22) 56%, rgba(7,7,7,0) 76%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(7,7,7,0.35) 0%, transparent 16%, transparent 78%, rgba(7,7,7,0.96) 100%)",
          }}
        />
        {/* Extra darken on mobile so text overlay is readable */}
        <div className="absolute inset-0 bg-[rgba(7,7,7,0.5)] md:hidden" />
      </div>

      <div
        className="relative z-10 pc-container min-h-[78vh] md:min-h-screen flex flex-col justify-center"
        style={{ paddingTop: "160px", paddingBottom: "60px" }}
      >
        <div style={{ maxWidth: "620px" }}>
          <div ref={head.ref} className={`reveal ${head.revealed ? "is-in" : ""}`}>
            <h1
              className="headline-xl"
              style={{
                fontSize: "clamp(31px, 3.7vw, 55px)",
                letterSpacing: "0.18em",
                lineHeight: 1.14,
                maxWidth: 540,
              }}
            >
              Elige cómo quieres permanecer.
            </h1>
          </div>
          <p
            ref={body.ref}
            className={`body-soft mt-9 max-w-[440px] reveal delay-1 ${body.revealed ? "is-in" : ""}`}
            style={{ fontSize: 16.5 }}
          >
            Concebimos cada obra para su lugar, su luz y para ti. Escoge el
            formato que se ajusta a tu visión.
          </p>

          <div
            ref={choices.ref}
            className="mt-14 grid grid-cols-2 gap-0 max-w-[540px] border-t border-[var(--pc-line)]"
          >
            <HeroChoice
              href="#residencias"
              label="Residencias"
              body="Tu casa de autor, pensada al milímetro para una sola familia."
              show={choices.revealed}
              delayCls="delay-1"
              testId="choice-residencias"
            />
            <HeroChoice
              href="#enclaves"
              label="Enclaves"
              body="Comunidades privadas donde diseñamos contigo la arquitectura y el territorio."
              show={choices.revealed}
              delayCls="delay-2"
              divider
              testId="choice-enclaves"
            />
          </div>

          <div
            ref={cta.ref}
            className={`mt-14 reveal delay-2 ${cta.revealed ? "is-in" : ""}`}
          >
            <CTA testId="proyectos-hero-cta" to="/contacto">
              Conversemos sobre tu proyecto
            </CTA>
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroChoice({ href, label, body, show, delayCls = "", divider = false, testId }) {
  return (
    <a
      href={href}
      data-testid={testId}
      className={`block py-7 reveal ${delayCls} ${show ? "is-in" : ""} ${
        divider ? "border-l border-[var(--pc-line)] pl-6" : "pr-6"
      } group`}
    >
      <div
        className="text-[var(--pc-text)] group-hover:text-[var(--pc-bronze-bright)] transition-colors"
        style={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 300,
          fontSize: 14,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <p
        className="body-soft mt-3"
        style={{ fontSize: 13.5, lineHeight: 1.6 }}
      >
        {body}
      </p>
      <div className="mt-5 inline-flex items-center gap-3 text-[var(--pc-text-soft)] group-hover:text-[var(--pc-bronze-bright)] transition-all duration-500">
        <span className="block h-px w-7 bg-current group-hover:w-10 transition-all duration-500" />
        <span className="block h-px w-2 rotate-45 origin-right bg-current" />
      </div>
    </a>
  );
}

/* ============================== Category Feature ==============================
   Both sections share the same alignment (text left, image right)
   and use a black-to-transparent fade instead of a hard cut.
================================================================================ */
function CategoryFeature({
  id,
  testId,
  number,
  title,
  body,
  image,
  ctaLabel,
  ctaTo,
  ctaTestId,
}) {
  const num = useReveal({ threshold: 0.3 });
  const head = useReveal();
  const bod = useReveal();
  const cta = useReveal();
  const img = useReveal({ threshold: 0.12 });

  return (
    <section
      id={id}
      data-testid={testId}
      className="relative w-full overflow-hidden border-t border-[var(--pc-line)]"
      style={{ minHeight: "640px" }}
    >
      {/* Full-bleed image with black fade on the left */}
      <div
        ref={img.ref}
        className={`absolute inset-0 reveal-img ${img.revealed ? "is-in" : ""}`}
      >
        <img
          src={image}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover"
          draggable="false"
        />
        {/* Black fade — text area lives on the left */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(7,7,7,0.97) 0%, rgba(7,7,7,0.92) 24%, rgba(7,7,7,0.62) 40%, rgba(7,7,7,0.22) 58%, rgba(7,7,7,0) 78%)",
          }}
        />
        {/* Soft vertical vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(7,7,7,0.25) 0%, transparent 14%, transparent 84%, rgba(7,7,7,0.65) 100%)",
          }}
        />
        {/* Extra darken on mobile so text is readable */}
        <div className="absolute inset-0 bg-[rgba(7,7,7,0.5)] md:hidden" />
      </div>

      <div className="relative z-10 pc-container py-20 md:py-24 lg:py-32 min-h-[640px] flex items-center">
        <div className="max-w-[460px]">
          <div ref={num.ref} className={`reveal ${num.revealed ? "is-in" : ""}`}>
            <div
              className="text-[var(--pc-bronze)]"
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 300,
                fontSize: 12,
                letterSpacing: "0.32em",
              }}
            >
              {number}
            </div>
            <div className={`category-hairline ${num.revealed ? "is-in" : ""}`} />
          </div>
          <div ref={head.ref} className={`reveal delay-1 ${head.revealed ? "is-in" : ""}`}>
            <h2
              className="headline-lg"
              style={{
                fontSize: "clamp(30px, 3.2vw, 46px)",
                letterSpacing: "0.16em",
                maxWidth: 420,
              }}
            >
              {title}
            </h2>
          </div>
          <p
            ref={bod.ref}
            className={`body-soft mt-9 max-w-[420px] reveal delay-2 ${bod.revealed ? "is-in" : ""}`}
          >
            {body}
          </p>
          <div
            ref={cta.ref}
            className={`mt-12 reveal delay-3 ${cta.revealed ? "is-in" : ""}`}
          >
            <CTA testId={ctaTestId} to={ctaTo}>
              {ctaLabel}
            </CTA>
          </div>
        </div>
      </div>
    </section>
  );
}
